/**
 * "What CEPI is answering from" card (spec §9.3). Presentational.
 *
 * The rule it must never break: an unusable or unconfirmed scope is never
 * rendered as a run with zero results or as "Whole label corpus". Either
 * would tell the reader something CEPI is not actually doing.
 */
import React from 'react';
import type { ActiveResult } from '../../services/activeResult';
import { attachmentKindName, attachmentTitle, type SourceKind } from '../../shell/cepiScope';

export interface ScopeCardProps {
  kind: SourceKind;
  /** Loaded attachment; read for `attachment` and `unavailable`. */
  result?: ActiveResult | null;
  /** Title of the tab the attachment came from, when that tab is still open. */
  sourceTabTitle?: string | undefined;
  /** Document source: published export title or mode name. */
  documentTitle?: string | undefined;
  /** A scope mutation is in flight: X and Remove are disabled. */
  busy?: boolean;
  detachError?: boolean;
  onRetry?: () => void;
  /** X on an attachment, and Remove on an unavailable one. */
  onDetach?: () => void;
}

const BASE = 'mx-2 mb-1 rounded border-2 border-ink bg-white px-2.5 py-1.5 text-[12px] text-ink';

export const ScopeCard: React.FC<ScopeCardProps> = ({
  kind, result, sourceTabTitle, documentTitle, busy = false, detachError = false,
  onRetry, onDetach,
}) => {
  if (kind === 'checking') {
    return (
      <div data-testid="cepi-scope-card" data-source="checking" className={BASE}>
        Checking what CEPI is answering from…
      </div>
    );
  }
  if (kind === 'error') {
    return (
      <div data-testid="cepi-scope-card" data-source="error" className={BASE}>
        <span>Couldn't confirm what CEPI is answering from</span>
        <button
          type="button"
          data-testid="cepi-scope-retry"
          onClick={onRetry}
          className="ml-2 underline underline-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }
  if (kind === 'unavailable') {
    return (
      <div
        data-testid="cepi-scope-card"
        data-source="unavailable"
        className="mx-2 mb-1 rounded border-2 border-warn-line bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn-ink"
      >
        <div data-testid="active-result-unavailable" data-state={result?.state}>
          <span>⚠ {result ? attachmentKindName(result) : 'Investigator'} result unavailable</span>
          <button
            type="button"
            onClick={onDetach}
            disabled={busy}
            className="ml-2 underline underline-offset-2 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
        {detachError && <DetachError />}
      </div>
    );
  }
  if (kind === 'attachment') {
    if (!result) return null;
    const { text, complete } = attachmentTitle(result);
    const criterion = result.criterion ? `“${result.criterion}”` : '';
    const subline = sourceTabTitle
      ? `${sourceTabTitle} tab${criterion ? ` · ${criterion}` : ''}`
      : criterion;
    return (
      <div
        data-testid="cepi-scope-card"
        data-source="attachment"
        data-complete={String(complete)}
        className={`${BASE} flex items-start gap-2`}
      >
        <div className="min-w-0 flex-1">
          <div className="font-medium">{text} <span aria-hidden>{complete ? '✓' : '⚠'}</span></div>
          {subline && <div className="truncate text-ink-2">{subline}</div>}
          {detachError && <DetachError />}
        </div>
        <button
          type="button"
          aria-label="Stop answering from these results"
          onClick={onDetach}
          disabled={busy}
          className="text-ink-2 hover:text-ink disabled:opacity-50"
        >
          ×
        </button>
      </div>
    );
  }
  if (kind === 'document') {
    return (
      <div data-testid="cepi-scope-card" data-source="document" className={BASE}>
        <div className="font-medium">{documentTitle}</div>
        <div className="text-ink-2">This tab</div>
      </div>
    );
  }
  return (
    <div data-testid="cepi-scope-card" data-source="corpus" className={BASE}>
      <div className="font-medium">Whole label corpus</div>
      <div className="text-ink-2">All labels in Precepi</div>
    </div>
  );
};

const DetachError: React.FC = () => (
  <div data-testid="cepi-detach-error" className="text-warn-ink">
    Couldn't stop answering from these results
  </div>
);

export default ScopeCard;
